/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ValueData, ValueModel, ValueFieldKey } from './value.component-model';
import { ViewComponentKey } from '../key/view-component.key';
import { ComponentFieldKey } from '../model/component.model';

export type ChangeMode = 'immediate' | 'triggered' | 'delayed';

export class ChangeModes {
  static readonly IMMEDIATE: ChangeMode = 'immediate';
  static readonly TRIGGERED: ChangeMode = 'triggered';
  static readonly DELAYED: ChangeMode = 'delayed';
}

export interface TextInputData extends ValueData {
  changeMode?: ChangeMode;
  changeDelay?: number;
  autocompleteToken?: string;
  isObscured?: boolean;
  placeholder?: string | boolean;
  autoselect?: boolean;
}

export class TextInputFieldKey {
  static readonly CHANGE_MODE = 'changeMode';
  static readonly CHANGE_DELAY = 'changeDelay';
  static readonly AUTOCOMPLETE_TOKEN = 'autocompleteToken';
  static readonly IS_OBSCURED = 'isObscured';
  static readonly PLACEHOLDER = 'placeholder';
  static readonly AUTOSELECT = 'autoselect';
}

export class TextInputModel extends ValueModel implements TextInputData {
  static readonly KEYS = ValueModel.KEYS.concat(
    Object.keys(TextInputFieldKey).map((key) => TextInputFieldKey[key])
  );

  constructor(data?: TextInputData, keys?: string[]) {
    super(data, keys || TextInputModel.KEYS);
  }

  get changeMode(): ChangeMode {
    return this.get(TextInputFieldKey.CHANGE_MODE, ChangeModes.TRIGGERED);
  }

  set changeMode(value: ChangeMode) {
    this.set(TextInputFieldKey.CHANGE_MODE, value);
  }

  get changeDelay(): number {
    return this.get(TextInputFieldKey.CHANGE_DELAY, 300);
  }

  set changeDelay(value: number) {
    this.set(TextInputFieldKey.CHANGE_DELAY, value);
  }

  get autocompleteToken(): string {
    return this.get(TextInputFieldKey.AUTOCOMPLETE_TOKEN);
  }

  set autocompleteToken(value: string) {
    this.set(TextInputFieldKey.AUTOCOMPLETE_TOKEN, value);
  }

  get isObscured(): boolean {
    return this.get(TextInputFieldKey.IS_OBSCURED);
  }

  set isObscured(value: boolean) {
    this.set(TextInputFieldKey.IS_OBSCURED, value);
  }

  get placeholder(): string | boolean {
    return this.get(TextInputFieldKey.PLACEHOLDER);
  }

  set placeholder(value: string | boolean) {
    this.set(TextInputFieldKey.PLACEHOLDER, value);
  }

  get autoselect(): boolean {
    return this.get(TextInputFieldKey.AUTOSELECT);
  }

  set autoselect(value: boolean) {
    this.set(TextInputFieldKey.AUTOSELECT, value);
  }

  protected setupValues(values: TextInputData, exclusive: boolean): TextInputData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.TEXT_INPUT;
    }
    if (exclusive && !(ComponentFieldKey.TAB_INDEX in values)) {
      values.tabIndex = 0;
    }
    if (exclusive && !(TextInputFieldKey.CHANGE_MODE in values)) {
      values.changeMode = ChangeModes.TRIGGERED;
    }
    if (exclusive && !(ValueFieldKey.FORMAT_ORIGINAL in values)) {
      // re-format changed formatted value
      values.formatOriginal = values.changeMode === ChangeModes.TRIGGERED;
    }
    if (exclusive && !(TextInputFieldKey.AUTOSELECT in values)) {
      values.autoselect = true;
    }
    return super.setupValues(values, exclusive) as TextInputData;
  }
}
